import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Toaster, toast } from "sonner";
import { Download, Play, Loader2, Globe2, RefreshCw, Search, Users, Mail,
  CheckCircle2, Clock, Upload, Instagram, MapPin, Sparkles, AtSign,
  Phone, Linkedin,
} from "lucide-react";
import { Logo, LogoWordmark } from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.withCredentials = true; // send the session cookie on API calls
const STATUSES = ["new", "approved", "queued", "sent", "replied", "rejected", "bounced"];

// One-click multi-channel outreach actions for a contact row. Each icon only
// renders when the underlying datum exists; LinkedIn falls back to a name+company
// search when no direct profile URL is on file.
function ChannelActions({ row }) {
  const phone = (row.phone || row.company_phone || "").trim();
  const igRaw = (row.instagram_handle || "").trim().replace(/^@/, "");
  const ig = igRaw && !igRaw.includes(" ") ? igRaw : "";
  const li = (row.linkedin_url || row.company_linkedin_url || "").trim();
  const searchName = [row.first_name || row.key_contact_first_name, row.last_name || row.key_contact_last_name]
    .filter(Boolean).join(" ").trim();
  const liSearchKeywords = [searchName, row.company_name].filter(Boolean).join(" ").trim();
  const liHref = li || (liSearchKeywords
    ? `https://www.linkedin.com/search/results/all/?keywords=${encodeURIComponent(liSearchKeywords)}`
    : "");

  const btn = "inline-flex h-6 w-6 items-center justify-center rounded-full border border-stone-300 text-stone-600 hover:bg-stone-100 hover:text-stone-900 transition-colors";
  return (
    <div className="flex items-center gap-1">
      {phone ? (
        <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} title={`Call ${phone}`} className={btn} data-testid={`ch-call-${row.id}`}>
          <Phone className="h-3 w-3" />
        </a>
      ) : null}
      {ig ? (
        <a href={`https://ig.me/m/${ig}`} target="_blank" rel="noreferrer" title={`DM @${ig} on Instagram`} className={btn} data-testid={`ch-ig-${row.id}`}>
          <Instagram className="h-3 w-3" />
        </a>
      ) : null}
      {liHref ? (
        <a
          href={liHref}
          target="_blank"
          rel="noreferrer"
          title={li ? "Open LinkedIn profile" : "Search on LinkedIn"}
          className={li ? `${btn} border-[#0a66c2]/40 text-[#0a66c2] hover:bg-[#0a66c2]/10` : `${btn} opacity-60`}
          data-testid={`ch-li-${row.id}`}
        >
          <Linkedin className="h-3 w-3" />
        </a>
      ) : null}
    </div>
  );
}

// ============================================================================
// SCRAPER TAB
// ============================================================================
function ScraperTab({ onScrapeDone }) {
  const [allCountries, setAllCountries] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [maxPer, setMaxPer] = useState(20);
  const [job, setJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [polling, setPolling] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);

  useEffect(() => {
    axios.get(`${API}/scraper/countries`).then((r) => {
      setAllCountries(r.data.countries || []);
      setSelected(new Set(r.data.countries || []));
    });
    refreshJobs();
  }, []);

  const refreshJobs = async () => {
    try {
      const r = await axios.get(`${API}/scraper/jobs`);
      setJobs(r.data || []);
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    if (!job || job.status === "done" || job.status === "error") {
      setPolling(false);
      return;
    }
    setPolling(true);
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/scraper/jobs/${job.id}`);
        setJob(r.data);
        if (r.data.result_count > 0) {
          const rr = await axios.get(`${API}/scraper/jobs/${job.id}/results`);
          setPreviewRows(rr.data.results || []);
        }
        if (r.data.status === "done" || r.data.status === "error") {
          clearInterval(t);
          setPolling(false);
          refreshJobs();
          onScrapeDone?.();
          toast(r.data.status === "done" ? "Scrape complete" : "Scrape failed", {
            description: r.data.message,
          });
        }
      } catch {
        /* noop */
      }
    }, 3500);
    return () => clearInterval(t);
  }, [job?.id]);

  const toggle = (c) => {
    const next = new Set(selected);
    if (next.has(c)) next.delete(c);
    else next.add(c);
    setSelected(next);
  };

  const startScrape = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one country");
      return;
    }
    try {
      const r = await axios.post(`${API}/scraper/start`, {
        countries: Array.from(selected),
        max_companies_per_country: Number(maxPer) || 20,
      });
      setJob(r.data);
      setPreviewRows([]);
      toast.success("Scrape started", { description: `Job ${r.data.id.slice(0, 8)}` });
    } catch (e) {
      toast.error("Could not start", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  };

  const downloadUrl = (id, fmt) => `${API}/scraper/jobs/${id}/download?fmt=${fmt}`;

  const overallPct = useMemo(() => {
    if (!job) return 0;
    if (job.status === "done") return 100;
    const total = job.progress_total || 1;
    return Math.min(100, Math.round((job.progress_done / total) * 100));
  }, [job]);

  return (
    <div className="space-y-8">
      <Card data-testid="config-card" className="border-stone-300 bg-white/70 backdrop-blur">
        <CardHeader>
          <CardTitle className="font-serif text-2xl flex items-center gap-2">
            <Globe2 className="h-5 w-5" /> Target Markets
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap gap-2" data-testid="country-list">
            {allCountries.map((c) => {
              const active = selected.has(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggle(c)}
                  data-testid={`country-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                    active
                      ? "bg-stone-900 text-amber-50 border-stone-900"
                      : "bg-transparent text-stone-700 border-stone-300 hover:border-stone-500"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs uppercase tracking-wider text-stone-500">
                Max companies / country
              </label>
              <Input
                type="number"
                min={1}
                max={5000}
                value={maxPer}
                onChange={(e) => setMaxPer(e.target.value)}
                className="w-40 bg-white border-stone-300"
                data-testid="max-companies-input"
              />
              <span className="text-[11px] text-stone-500 mt-1">
                Chunked (25 / call). Realistically tops out around 50-150 per country.
              </span>
            </div>
            <Button
              onClick={startScrape}
              disabled={polling}
              data-testid="run-scrape-btn"
              className="bg-stone-900 hover:bg-stone-800 text-amber-50 rounded-full px-6"
            >
              {polling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              {polling ? "Scraping…" : "Run scrape"}
            </Button>
            <Button
              variant="outline"
              onClick={refreshJobs}
              className="rounded-full border-stone-300"
              data-testid="refresh-jobs-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {job && (
        <Card data-testid="job-card" className="border-stone-300 bg-white/70">
          <CardHeader>
            <CardTitle className="font-serif text-2xl flex items-center justify-between">
              <span>Job {job.id.slice(0, 8)}</span>
              <Badge
                data-testid="job-status"
                className={
                  job.status === "done"
                    ? "bg-emerald-700 text-amber-50"
                    : job.status === "error"
                    ? "bg-red-700 text-amber-50"
                    : "bg-stone-700 text-amber-50"
                }
              >
                {job.status}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-stone-600" data-testid="job-message">{job.message}</p>
            <Progress value={overallPct} data-testid="job-progress" />
            {job.rejected_count > 0 && (
              <p className="text-xs text-stone-500" data-testid="rejected-summary">
                {job.rejected_count} candidate{job.rejected_count === 1 ? "" : "s"} rejected (unreachable URL or company name not found on page).
              </p>
            )}
            <div className="flex flex-wrap gap-3 pt-2">
              <a
                href={downloadUrl(job.id, "csv")}
                target="_blank"
                rel="noreferrer"
                data-testid="download-csv-link"
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border ${
                  job.csv_available
                    ? "bg-stone-900 text-amber-50 border-stone-900"
                    : "pointer-events-none opacity-40 border-stone-300"
                }`}
              >
                <Download className="h-4 w-4" /> Download CSV
              </a>
              <a
                href={downloadUrl(job.id, "xlsx")}
                target="_blank"
                rel="noreferrer"
                data-testid="download-xlsx-link"
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border ${
                  job.xlsx_available
                    ? "bg-stone-900 text-amber-50 border-stone-900"
                    : "pointer-events-none opacity-40 border-stone-300"
                }`}
              >
                <Download className="h-4 w-4" /> Download XLSX
              </a>
            </div>

            {previewRows.length > 0 && (
              <div className="overflow-x-auto mt-6 border border-stone-200 rounded-lg">
                <table className="min-w-full text-sm" data-testid="results-table">
                  <thead className="bg-stone-100 text-stone-700">
                    <tr>
                      <th className="text-left px-3 py-2">Company</th>
                      <th className="text-left px-3 py-2">Country</th>
                      <th className="text-left px-3 py-2">URL</th>
                      <th className="text-left px-3 py-2">Contact title</th>
                      <th className="text-left px-3 py-2">Email</th>
                      <th className="text-left px-3 py-2">Stores</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.slice(0, 50).map((r, idx) => (
                      <tr key={idx} className="border-t border-stone-200 hover:bg-stone-50">
                        <td className="px-3 py-2 font-medium">{r.company_name}</td>
                        <td className="px-3 py-2">{r.country}</td>
                        <td className="px-3 py-2 max-w-[220px] truncate">
                          <a className="underline text-stone-700" href={r.url} target="_blank" rel="noreferrer">
                            {r.url}
                          </a>
                        </td>
                        <td className="px-3 py-2">{r.key_contact_title}</td>
                        <td className="px-3 py-2">{r.contact_email_address || r.company_email || r.key_contact_email}</td>
                        <td className="px-3 py-2">{r.number_of_stores}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {jobs.length > 0 && (
        <Card data-testid="history-card" className="border-stone-300 bg-white/70">
          <CardHeader>
            <CardTitle className="font-serif text-xl">Recent jobs</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-stone-200">
              {jobs.map((j) => (
                <li key={j.id} className="flex items-center justify-between py-3 text-sm" data-testid={`history-row-${j.id}`}>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setJob(j)} className="font-mono underline text-stone-700">
                      {j.id.slice(0, 8)}
                    </button>
                    <Badge variant="outline" className="border-stone-300">{j.status}</Badge>
                    <span className="text-stone-500">
                      {j.result_count} companies · {j.countries.length} markets
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {j.csv_available && (
                      <a href={downloadUrl(j.id, "csv")} className="text-stone-700 underline" target="_blank" rel="noreferrer">CSV</a>
                    )}
                    {j.xlsx_available && (
                      <a href={downloadUrl(j.id, "xlsx")} className="text-stone-700 underline" target="_blank" rel="noreferrer">XLSX</a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// CONTACTS TAB
// ============================================================================
function ContactsTab({ refreshSignal }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [countries, setCountries] = useState([]);

  const [fCompany, setFCompany] = useState("");
  const [fEmail, setFEmail] = useState("");
  const [fCountry, setFCountry] = useState("all");
  const [fStatus, setFStatus] = useState("new");
  const [fVerified, setFVerified] = useState("all");
  const [viewTab, setViewTab] = useState("active"); // active | rejected
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const visibleRows = useMemo(
    () => rows.filter((r) => (viewTab === "rejected" ? r.status === "rejected" : r.status !== "rejected")),
    [rows, viewTab],
  );

  const toggleRowSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllVisible = () => {
    const allIds = new Set(visibleRows.map((r) => r.id));
    const allSelected = visibleRows.length > 0 && visibleRows.every((r) => selectedIds.has(r.id));
    if (allSelected) {
      // clear only the visible ones
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => new Set([...prev, ...allIds]));
    }
  };

  const bulkUpdate = async (status) => {
    if (selectedIds.size === 0) {
      toast.error("Nothing selected");
      return;
    }
    if (!window.confirm(`Mark ${selectedIds.size} selected contact(s) as "${status}"?`)) return;
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    // Chunk to keep each request small enough to travel through Cloudflare cleanly
    const CHUNK = 500;
    let totalContacts = 0;
    let totalCompanies = 0;
    let failed = 0;
    try {
      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK);
        try {
          const r = await axios.patch(`${API}/crm/contacts/bulk-status`, { ids: chunk, status });
          totalContacts += r.data.updated_contacts || 0;
          totalCompanies += r.data.updated_companies || 0;
        } catch {
          failed += chunk.length;
        }
      }
      setRows((prev) => prev.map((r) => (selectedIds.has(r.id) ? { ...r, status } : r)));
      setSelectedIds(new Set());
      toast.success(`Marked ${totalContacts + totalCompanies} as ${status}`, {
        description: `${totalContacts} contacts · ${totalCompanies} company-only rows${failed ? ` · ${failed} failed` : ""}`,
      });
      refreshSummary();
    } catch (e) {
      toast.error("Bulk update failed", { description: e?.message || "" });
    } finally {
      setBulkBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fCompany) params.set("company_name", fCompany);
      if (fEmail) params.set("email", fEmail);
      if (fCountry !== "all") params.set("country", fCountry);
      if (fStatus !== "all") params.set("status", fStatus);
      if (fVerified !== "all") params.set("verified", fVerified);
      // Server returns all matching rows; we filter by view-tab client-side so
      // counts in the sub-tab labels stay accurate.
      params.set("limit", "5000");
      const r = await axios.get(`${API}/crm/contacts?${params.toString()}`);
      setRows(r.data.rows || []);
      setCount(r.data.count || 0);
      const s = await axios.get(`${API}/crm/summary`);
      setSummary(s.data || {});
      const cs = await axios.get(`${API}/crm/countries`);
      setCountries(cs.data.countries || []);
    } finally {
      setLoading(false);
    }
  }, [fCompany, fEmail, fCountry, fStatus, fVerified]);

  useEffect(() => { load(); }, [load, refreshSignal]);

  const refreshSummary = useCallback(async () => {
    try {
      const s = await axios.get(`${API}/crm/summary`);
      setSummary(s.data || {});
    } catch {/* ignore */}
  }, []);

  const sendOne = async (id) => {
    const target = rows.find((r) => r.id === id);
    if (!target) return;
    const isResend = target.status === "sent";
    const ok = window.confirm(
      isResend
        ? `Resend the outreach email to ${target.email}?\n\nThis contact has already been emailed once — sending again will create a new entry in the Sent log.`
        : `Send the outreach email to ${target.email} now?\n\nUses the template + signature from the Outreach tab.`,
    );
    if (!ok) return;
    const toastId = toast.loading(`Sending to ${target.email}…`);
    try {
      const r = await axios.post(`${API}/outreach/send`, {
        contact_ids: [id],
        campaign_name: `Manual send · ${target.company_name || target.email}`,
      });
      const jobId = r.data.job_id;
      const poll = setInterval(async () => {
        try {
          const j = await axios.get(`${API}/outreach/send/${jobId}`);
          const s = j.data;
          if (s.status === "done") {
            clearInterval(poll);
            const res = (s.results || [])[0];
            if (res?.status === "sent") {
              toast.success(`Email sent to ${target.email}`, {
                id: toastId,
                description: target.company_name || "",
              });
              setRows((prev) => prev.map((x) => (x.id === id ? { ...x, status: "sent" } : x)));
            } else if (res?.status === "skipped") {
              toast.error("Skipped", { id: toastId, description: res.reason || "" });
            } else {
              toast.error("Send failed", { id: toastId, description: res?.error || "Unknown error" });
            }
          }
        } catch {/* ignore */}
      }, 2000);
    } catch (e) {
      toast.error("Send failed", {
        id: toastId,
        description: e?.response?.data?.detail || e.message,
      });
    }
  };

  const updateStatus = async (id, status) => {
    try {
      // If we're rejecting/approving a contact and the company has more
      // than one row in the table, ask whether to apply to ALL of them.
      let propagate = false;
      const current = rows.find((r) => r.id === id);
      if (current && (status === "rejected" || status === "approved")) {
        const sameCompany = rows.filter(
          (r) => r.company_id && r.company_id === current.company_id,
        );
        if (sameCompany.length > 1) {
          propagate = window.confirm(
            `${current.company_name} has ${sameCompany.length} entries in the list.\n\n` +
            `Mark ALL of them as "${status}"?\n\n` +
            `OK = update all ${sameCompany.length}\n` +
            `Cancel = just this one row`,
          );
        }
      }

      if (propagate && current?.company_id) {
        const targetIds = rows
          .filter((r) => r.company_id === current.company_id)
          .map((r) => r.id);
        // Best-effort parallel; ignore individual failures
        const results = await Promise.allSettled(
          targetIds.map((tid) => axios.patch(`${API}/crm/contacts/${tid}/status`, { status })),
        );
        const ok = results.filter((r) => r.status === "fulfilled").length;
        setRows((prev) => prev.map((r) => (r.company_id === current.company_id ? { ...r, status } : r)));
        toast.success(`Marked ${ok} entries as ${status}`, {
          description: `${current.company_name}`,
        });
      } else {
        await axios.patch(`${API}/crm/contacts/${id}/status`, { status });
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
        toast.success(`Marked ${status}`);
      }
      refreshSummary();
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const exportUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (fCompany) params.set("company_name", fCompany);
    if (fEmail) params.set("email", fEmail);
    if (fCountry !== "all") params.set("country", fCountry);
    if (fStatus !== "all") params.set("status", fStatus);
    if (fVerified !== "all") params.set("verified", fVerified);
    return `${API}/crm/contacts/export?${params.toString()}`;
  }, [fCompany, fEmail, fCountry, fStatus, fVerified]);

  // Meta Custom Audience / LinkedIn Matched Audience upload file. Mirrors the
  // active filters; defaults to approved contacts when no status filter is set.
  const audienceUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (fCompany) params.set("company_name", fCompany);
    if (fEmail) params.set("email", fEmail);
    if (fCountry !== "all") params.set("country", fCountry);
    params.set("status", fStatus === "all" ? "all" : fStatus);
    if (fVerified !== "all") params.set("verified", fVerified);
    return `${API}/crm/contacts/export-audience?${params.toString()}`;
  }, [fCompany, fEmail, fCountry, fStatus, fVerified]);

  const [importing, setImporting] = useState(false);
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/crm/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = r.data;
      toast.success("Import complete", {
        description: `${d.contacts_new} new contacts, ${d.companies_new} new companies, ${d.contacts_skipped} skipped.`,
      });
      load();
    } catch (err) {
      toast.error("Import failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  // Poll an enrichment job until done. Surfaces progress via toasts.
  const pollEnrichJob = async (jobId, label) => {
    const toastId = toast.loading(`${label} starting…`);
    let last = "";
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 4000));
      try {
        const r = await axios.get(`${API}/crm/enrich/jobs/${jobId}`);
        const j = r.data;
        if (j.message !== last) {
          toast.loading(`${label}: ${j.message}`, { id: toastId });
          last = j.message;
        }
        if (j.status === "done") {
          toast.success(`${label} done`, { id: toastId, description: j.message });
          load();
          return;
        }
        if (j.status === "error") {
          toast.error(`${label} failed`, { id: toastId, description: j.error || j.message });
          return;
        }
      } catch {/* ignore */}
    }
    toast.dismiss(toastId);
  };

  const enrichInstagram = async () => {
    try {
      const r = await axios.post(`${API}/crm/enrich/instagram?limit=100`);
      pollEnrichJob(r.data.id, "Instagram enrichment");
    } catch (e) {
      toast.error("Could not start", { description: e?.response?.data?.detail || e.message });
    }
  };

  const enrichGmaps = async () => {
    try {
      const r = await axios.post(`${API}/crm/enrich/gmaps`, { limit: 30 });
      pollEnrichJob(r.data.id, "Google Maps enrichment");
    } catch (e) {
      toast.error("Could not start", { description: e?.response?.data?.detail || e.message });
    }
  };

  const [enrichEmailState, setEnrichEmailState] = useState(null);
  const enrichEmailToastId = useRef(null);
  const findEmails = async () => {
    try {
      const r = await axios.post(`${API}/crm/enrich/start`);
      if (!r.data.started && r.data.status?.running) {
        toast.message("Email enrichment already running", {
          description: `${r.data.status.processed}/${r.data.status.total} processed`,
        });
      } else {
        enrichEmailToastId.current = toast.loading("Finding emails…", {
          description: "Scraping company websites for contact addresses.",
        });
      }
      setEnrichEmailState(r.data.status);
    } catch (e) {
      toast.error("Could not start", { description: e?.response?.data?.detail || e.message });
    }
  };

  // Poll email-enrichment status while a run is active. Updates the toast
  // and refreshes the contacts list whenever new contacts get created.
  useEffect(() => {
    if (!enrichEmailState?.running) return undefined;
    let lastCreated = enrichEmailState.contacts_created || 0;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/crm/enrich/status`);
        const s = r.data;
        setEnrichEmailState(s);
        if (enrichEmailToastId.current) {
          toast.loading(
            `Emails: ${s.processed}/${s.total} • ${s.contacts_created} contacts created`,
            {
              id: enrichEmailToastId.current,
              description: s.current_company ? `Scraping ${s.current_company}…` : "",
            },
          );
        }
        if (s.contacts_created > lastCreated) {
          lastCreated = s.contacts_created;
          load();
        }
        if (!s.running) {
          clearInterval(id);
          if (enrichEmailToastId.current) {
            toast.success("Email enrichment complete", {
              id: enrichEmailToastId.current,
              description: `${s.contacts_created} new contacts created from ${s.processed} companies (${s.failed} failed, ${s.no_website} no website).`,
            });
            enrichEmailToastId.current = null;
          }
          load();
        }
      } catch {/* ignore */}
    }, 4000);
    return () => clearInterval(id);
  }, [enrichEmailState?.running, load]);

  const [showDiscover, setShowDiscover] = useState(false);
  const [discoverQueries, setDiscoverQueries] = useState("bespoke tailor, made-to-measure suits");
  const [discoverCities, setDiscoverCities] = useState("London, Paris, Milan, New York");
  const [discoverCountry, setDiscoverCountry] = useState("");
  const [discoverMax, setDiscoverMax] = useState(10);
  const runDiscover = async () => {
    try {
      const r = await axios.post(`${API}/crm/discover/gmaps`, {
        queries: discoverQueries.split(",").map((s) => s.trim()).filter(Boolean),
        cities: discoverCities.split(",").map((s) => s.trim()).filter(Boolean),
        country: discoverCountry,
        max_per_query: Number(discoverMax) || 10,
      });
      setShowDiscover(false);
      pollEnrichJob(r.data.id, "GMB discovery");
    } catch (e) {
      toast.error("Could not start", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <SummaryStat icon={<Users className="h-4 w-4" />} label="Companies" value={summary.companies ?? 0} />
        <SummaryStat icon={<Mail className="h-4 w-4" />} label="Contacts" value={summary.contacts ?? 0} />
        <SummaryStat label="GMB leads" value={summary.leads_without_contact ?? 0} />
        <SummaryStat icon={<Clock className="h-4 w-4" />} label="New" value={summary.new ?? 0} />
        <SummaryStat icon={<CheckCircle2 className="h-4 w-4" />} label="Approved" value={summary.approved ?? 0} />
        <SummaryStat label="Queued" value={summary.queued ?? 0} />
        <SummaryStat label="Sent" value={summary.sent ?? 0} />
      </div>

      <Card className="border-stone-300 bg-white/70">
        <CardHeader>
          <CardTitle className="font-serif text-2xl flex items-center justify-between">
            <span>Contacts</span>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={findEmails}
                disabled={enrichEmailState?.running}
                className="rounded-full border-stone-300"
                data-testid="find-emails-btn"
              >
                {enrichEmailState?.running ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Finding emails… {enrichEmailState.processed}/{enrichEmailState.total}</>
                ) : (
                  <><AtSign className="h-4 w-4 mr-1" /> Find emails</>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={enrichInstagram}
                className="rounded-full border-stone-300"
                data-testid="enrich-instagram-btn"
              >
                <Instagram className="h-4 w-4 mr-1" /> Enrich Instagram
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={enrichGmaps}
                className="rounded-full border-stone-300"
                data-testid="enrich-gmaps-btn"
              >
                <MapPin className="h-4 w-4 mr-1" /> Enrich via Maps
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDiscover(true)}
                className="rounded-full border-stone-300"
                data-testid="discover-gmaps-btn"
              >
                <Sparkles className="h-4 w-4 mr-1" /> Discover via Maps
              </Button>
              <input
                type="file"
                accept=".csv"
                onChange={handleImport}
                className="hidden"
                id="import-csv-input"
                data-testid="import-csv-input"
              />
              <label
                htmlFor="import-csv-input"
                className={`inline-flex items-center gap-1 text-sm border border-stone-300 px-3 py-1.5 rounded-full cursor-pointer hover:bg-stone-50 ${importing ? "opacity-50 pointer-events-none" : ""}`}
                data-testid="import-csv-btn"
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Import CSV
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={load}
                className="rounded-full border-stone-300"
                data-testid="refresh-contacts-btn"
              >
                <RefreshCw className="h-4 w-4 mr-1" /> Refresh
              </Button>
              <a
                href={exportUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm bg-stone-900 text-amber-50 px-3 py-1.5 rounded-full"
                data-testid="export-contacts-csv"
              >
                <Download className="h-4 w-4" /> Export CSV
              </a>
              <a
                href={audienceUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm border border-stone-300 text-stone-700 hover:bg-stone-100 px-3 py-1.5 rounded-full transition-colors"
                data-testid="export-audience-csv"
                title="Download a Meta Custom Audience / LinkedIn Matched Audience upload file for the current filters"
              >
                <Users className="h-4 w-4" /> Ad audience
              </a>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Company contains…"
                value={fCompany}
                onChange={(e) => setFCompany(e.target.value)}
                className="pl-8 bg-white border-stone-300"
                data-testid="filter-company"
              />
            </div>
            <div className="relative">
              <Mail className="absolute left-2 top-2.5 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Email contains…"
                value={fEmail}
                onChange={(e) => setFEmail(e.target.value)}
                className="pl-8 bg-white border-stone-300"
                data-testid="filter-email"
              />
            </div>
            <Select value={fCountry} onValueChange={setFCountry}>
              <SelectTrigger data-testid="filter-country"><SelectValue placeholder="Country" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All countries</SelectItem>
                {countries.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={fStatus} onValueChange={setFStatus}>
              <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={fVerified} onValueChange={setFVerified}>
              <SelectTrigger data-testid="filter-verified"><SelectValue placeholder="Verified" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                <SelectItem value="true">Verified only</SelectItem>
                <SelectItem value="false">Unverified only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 mb-3 border-b border-stone-200" data-testid="contacts-subtabs">
            <button
              type="button"
              onClick={() => setViewTab("active")}
              data-testid="subtab-active"
              className={`px-4 py-2 text-sm transition-colors ${
                viewTab === "active"
                  ? "border-b-2 border-stone-900 text-stone-900 font-medium"
                  : "text-stone-500 hover:text-stone-700"
              }`}
            >
              Active <span className="text-xs text-stone-400 ml-1">{rows.filter((r) => r.status !== "rejected").length}</span>
            </button>
            <button
              type="button"
              onClick={() => setViewTab("rejected")}
              data-testid="subtab-rejected"
              className={`px-4 py-2 text-sm transition-colors ${
                viewTab === "rejected"
                  ? "border-b-2 border-stone-900 text-stone-900 font-medium"
                  : "text-stone-500 hover:text-stone-700"
              }`}
            >
              Rejected <span className="text-xs text-stone-400 ml-1">{rows.filter((r) => r.status === "rejected").length}</span>
            </button>
            {selectedIds.size > 0 && (
              <div className="ml-4 flex items-center gap-2" data-testid="bulk-action-bar">
                <span className="text-xs text-stone-700 font-medium">{selectedIds.size} selected</span>
                <Button size="sm" disabled={bulkBusy} onClick={() => bulkUpdate("approved")} className="rounded-full bg-emerald-700 text-amber-50 hover:bg-emerald-800" data-testid="bulk-approve-btn">
                  {bulkBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : "✓ "}Approve
                </Button>
                <Button size="sm" disabled={bulkBusy} onClick={() => bulkUpdate("rejected")} className="rounded-full bg-red-700 text-amber-50 hover:bg-red-800" data-testid="bulk-reject-btn">
                  {bulkBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : "✕ "}Reject
                </Button>
                <button onClick={() => setSelectedIds(new Set())} className="text-xs underline text-stone-500 hover:text-stone-900" data-testid="bulk-clear-btn">
                  Clear
                </button>
              </div>
            )}
            <p className="text-xs text-stone-500 ml-auto" data-testid="contact-count">
              {loading ? "Loading…" : `${count} total`}
            </p>
          </div>

          <div className="overflow-x-auto border border-stone-200 rounded-lg">
            <table className="min-w-full text-sm" data-testid="contacts-table">
              <thead className="bg-stone-100 text-stone-700 sticky top-0">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={visibleRows.length > 0 && visibleRows.every((r) => selectedIds.has(r.id))}
                      onChange={toggleSelectAllVisible}
                      data-testid="bulk-select-all"
                      title="Select all visible"
                    />
                  </th>
                  <th className="text-left px-3 py-2">Company</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Country</th>
                  <th className="text-left px-3 py-2">First name</th>
                  <th className="text-left px-3 py-2">Last name</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Phone</th>
                  <th className="text-left px-3 py-2">Instagram</th>
                  <th className="text-left px-3 py-2">Maps</th>
                  <th className="text-left px-3 py-2">Reach out</th>
                  <th className="text-left px-3 py-2">Contact title</th>
                  <th className="text-left px-3 py-2">Stores</th>
                  <th className="text-left px-3 py-2">Verified</th>
                  <th className="text-left px-3 py-2 w-32">Status</th>
                  <th className="text-left px-3 py-2">URL</th>
                  <th className="text-right px-3 py-2 sticky right-0 bg-stone-100 w-44">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => (
                  <tr key={r.id} className={`border-t border-stone-200 hover:bg-stone-50 ${selectedIds.has(r.id) ? "bg-amber-50/40" : ""}`} data-testid={`contact-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleRowSelect(r.id)}
                        data-testid={`row-check-${r.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-medium">{r.company_name}</td>
                    <td className="px-3 py-2 text-xs text-stone-600">{r.company_type || ""}</td>
                    <td className="px-3 py-2">{r.country}</td>
                    <td className="px-3 py-2">{r.first_name || r.key_contact_first_name || ""}</td>
                    <td className="px-3 py-2">{r.last_name || r.key_contact_last_name || ""}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {r.email}
                      {r.status === "bounced" && (
                        <Badge className="ml-2 bg-red-700 text-amber-50 align-middle" data-testid={`bounced-tag-${r.id}`}>Bounced</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.phone || r.company_phone || ""}</td>
                    <td className="px-3 py-2">
                      {r.instagram_handle ? (
                        <a
                          href={`https://instagram.com/${r.instagram_handle}`}
                          target="_blank"
                          rel="noreferrer"
                          className="underline text-stone-700"
                        >@{r.instagram_handle}</a>
                      ) : ""}
                    </td>
                    <td className="px-3 py-2">
                      {r.google_maps_url ? (
                        <a
                          href={r.google_maps_url}
                          target="_blank"
                          rel="noreferrer"
                          className="underline text-stone-700"
                        >Map</a>
                      ) : ""}
                    </td>
                    <td className="px-3 py-2"><ChannelActions row={r} /></td>
                    <td className="px-3 py-2">{r.title || r.key_contact_title}</td>
                    <td className="px-3 py-2">{r.number_of_stores}</td>
                    <td className="px-3 py-2">
                      {r.verified ? (
                        <Badge className="bg-emerald-700 text-amber-50">✓</Badge>
                      ) : (
                        <Badge variant="outline" className="border-stone-300">–</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                        <SelectTrigger
                          className="h-7 w-32 text-xs"
                          data-testid={`status-trigger-${r.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 max-w-[200px] truncate">
                      <a className="underline text-stone-700" href={r.company_url} target="_blank" rel="noreferrer">
                        {r.company_url}
                      </a>
                    </td>
                    <td className="px-3 py-2 sticky right-0 bg-white shadow-[-4px_0_4px_-4px_rgba(0,0,0,0.05)]">
                      <div className="flex items-center gap-1 justify-end">
                        {r.status === "new" ? (
                          <>
                            <button
                              onClick={() => updateStatus(r.id, "approved")}
                              data-testid={`approve-${r.id}`}
                              className="px-2 py-1 rounded-full text-xs bg-emerald-700 text-amber-50 hover:bg-emerald-800"
                              title="Approve"
                            >
                              ✓ Approve
                            </button>
                            <button
                              onClick={() => updateStatus(r.id, "rejected")}
                              data-testid={`reject-${r.id}`}
                              className="px-2 py-1 rounded-full text-xs bg-red-700 text-amber-50 hover:bg-red-800"
                              title="Reject"
                            >
                              ✕ Reject
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-stone-400 italic" data-testid={`acted-${r.id}`}>
                            {r.status === "sent" ? "sent ✓" : r.status === "replied" ? "replied" : "—"}
                          </span>
                        )}
                        {(r.email || "").includes("@") && !r.id.startsWith("company-") && (
                          <button
                            onClick={() => sendOne(r.id)}
                            data-testid={`send-${r.id}`}
                            className="px-2 py-1 rounded-full text-xs bg-stone-900 text-amber-50 hover:bg-stone-700"
                            title={r.status === "sent" ? "Send again" : "Send email"}
                          >
                            <Mail className="inline-block h-3 w-3 mr-1" />
                            {r.status === "sent" ? "Resend" : "Send"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!visibleRows.length && !loading && (
                  <tr><td colSpan={16} className="px-3 py-8 text-center text-stone-500">
                    {viewTab === "rejected"
                      ? "No rejected contacts."
                      : "No contacts yet. Run a scrape on the first tab."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showDiscover && (
        <div
          className="fixed inset-0 z-50 bg-stone-900/50 flex items-center justify-center p-4"
          onClick={() => setShowDiscover(false)}
          data-testid="discover-modal"
        >
          <div
            className="bg-[#f6f1e7] rounded-xl p-6 max-w-lg w-full border border-stone-300"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-2xl mb-1">Discover via Google Maps</h3>
            <p className="text-sm text-stone-600 mb-4">
              Each query is combined with each city. Example: <em>&quot;bespoke tailor in London&quot;</em>.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500">Queries (comma-separated)</label>
                <Input
                  value={discoverQueries}
                  onChange={(e) => setDiscoverQueries(e.target.value)}
                  className="bg-white border-stone-300 mt-1"
                  data-testid="discover-queries-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-stone-500">Cities (comma-separated)</label>
                <Input
                  value={discoverCities}
                  onChange={(e) => setDiscoverCities(e.target.value)}
                  className="bg-white border-stone-300 mt-1"
                  data-testid="discover-cities-input"
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs uppercase tracking-wider text-stone-500">Country (optional)</label>
                  <Input
                    value={discoverCountry}
                    onChange={(e) => setDiscoverCountry(e.target.value)}
                    placeholder="e.g. United Kingdom"
                    className="bg-white border-stone-300 mt-1"
                    data-testid="discover-country-input"
                  />
                </div>
                <div className="w-32">
                  <label className="text-xs uppercase tracking-wider text-stone-500">Max / query</label>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={discoverMax}
                    onChange={(e) => setDiscoverMax(e.target.value)}
                    className="bg-white border-stone-300 mt-1"
                    data-testid="discover-max-input"
                  />
                </div>
              </div>
              <p className="text-xs text-stone-500">
                Estimated places: <strong>{
                  (discoverQueries.split(",").filter((s) => s.trim()).length || 0)
                  * (discoverCities.split(",").filter((s) => s.trim()).length || 0)
                  * (Number(discoverMax) || 0)
                }</strong> (each ≈ $0.002 on Apify).
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowDiscover(false)} className="rounded-full border-stone-300">
                Cancel
              </Button>
              <Button onClick={runDiscover} className="bg-stone-900 text-amber-50 rounded-full" data-testid="discover-run-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Run discovery
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryStat({ icon, label, value }) {
  return (
    <div className="bg-white/70 border border-stone-200 rounded-lg p-3 flex items-center gap-3" data-testid={`stat-${label.toLowerCase()}`}>
      {icon && <span className="text-stone-700">{icon}</span>}
      <div>
        <div className="text-xs uppercase tracking-wider text-stone-500">{label}</div>
        <div className="text-xl font-serif">{value}</div>
      </div>
    </div>
  );
}

// ============================================================================
// DISCOVERY TAB
// ============================================================================
function DiscoveryTab({ refreshSignal }) {
  const [catalogue, setCatalogue] = useState({ cities: [], countries: [], queries_per_lang: {} });
  const [status, setStatus] = useState({});
  const [recent, setRecent] = useState([]);
  const [config, setConfig] = useState({ auto_enabled: true, resweep_days: 7, max_per_query: 20 });
  const [usage, setUsage] = useState(null);

  const loadAll = useCallback(async () => {
    const [cat, st, tasks, cfg] = await Promise.all([
      axios.get(`${API}/discovery/catalogue`),
      axios.get(`${API}/discovery/status`),
      axios.get(`${API}/discovery/tasks?limit=12`),
      axios.get(`${API}/discovery/config`),
    ]);
    setCatalogue(cat.data);
    setStatus(st.data);
    setRecent(tasks.data || []);
    setConfig(cfg.data);
    // Usage is fetched separately so a slow Apify call doesn't block the rest
    axios.get(`${API}/discovery/apify_usage`).then(
      (r) => setUsage(r.data),
      () => setUsage(null),
    );
  }, []);

  useEffect(() => { loadAll(); }, [loadAll, refreshSignal]);
  useEffect(() => {
    const t = setInterval(loadAll, 6000);
    return () => clearInterval(t);
  }, [loadAll]);

  const updateConfig = async (patch) => {
    try {
      const r = await axios.patch(`${API}/discovery/config`, patch);
      setConfig(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed", { description: e.message });
    }
  };

  const tickNow = async () => {
    try {
      const r = await axios.post(`${API}/discovery/auto/tick`);
      if (r.data?.tasks_added) {
        toast.success(`Enqueued ${r.data.tasks_added} tasks for ${r.data.city}, ${r.data.country}`);
      } else {
        toast.message(r.data?.message || "Nothing stale to enqueue.");
      }
      loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const requeueFailed = async () => {
    try {
      const r = await axios.post(`${API}/discovery/reset-failed`);
      toast.success(`Requeued ${r.data.requeued} failed tasks`);
      loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const total = (status.queued || 0) + (status.claimed || 0) + (status.running || 0) + (status.done || 0) + (status.failed || 0);
  const pct = total ? Math.round(((status.done || 0) + (status.failed || 0)) / total * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Live stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <SummaryStat label="Queued" value={status.queued ?? 0} />
        <SummaryStat label="Running" value={(status.running ?? 0) + (status.claimed ?? 0)} />
        <SummaryStat label="Done" value={status.done ?? 0} />
        <SummaryStat label="Failed" value={status.failed ?? 0} />
        <SummaryStat label="Places" value={status.places_returned ?? 0} />
        <SummaryStat label="New companies" value={status.new_companies ?? 0} />
        <SummaryStat
          label="Apify spend (cycle)"
          value={usage ? `$${(usage.usd_spent ?? 0).toFixed(2)}` : "…"}
        />
      </div>

      <Card className="border-stone-300 bg-white/70">
        <CardHeader>
          <CardTitle className="font-serif text-2xl flex items-center justify-between">
            <span>Auto-Discovery</span>
            <Badge
              className={
                config.auto_enabled && status.worker_running
                  ? "bg-emerald-700 text-amber-50"
                  : status.worker_running
                  ? "bg-amber-700 text-amber-50"
                  : "bg-stone-400 text-amber-50"
              }
              data-testid="worker-status"
            >
              {config.auto_enabled && status.worker_running
                ? "auto-on · running"
                : status.worker_running
                ? "manual · running"
                : "stopped"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-stone-600">
            The worker continuously cycles through <strong>{catalogue.cities.length} cities</strong> across{" "}
            <strong>{catalogue.countries.length} countries</strong> in{" "}
            <strong>{Object.keys(catalogue.queries_per_lang).length} languages</strong>.
            Each city is re-swept every <strong>{config.resweep_days} days</strong> so newly-opened ateliers get picked up automatically.
          </p>

          {total > 0 && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-stone-500">
                <span>Active queue progress</span><span>{pct}%</span>
              </div>
              <Progress value={pct} data-testid="sweep-progress" />
            </div>
          )}

          <div className="flex flex-wrap items-end gap-4">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="auto-toggle">
              <input
                type="checkbox"
                checked={!!config.auto_enabled}
                onChange={(e) => updateConfig({ auto_enabled: e.target.checked })}
                className="h-4 w-4"
              />
              <span className="text-sm">Auto-discovery enabled</span>
            </label>
            <div>
              <label className="text-xs uppercase tracking-wider text-stone-500">Re-sweep interval (days)</label>
              <Input
                type="number" min={1} max={90}
                value={config.resweep_days}
                onChange={(e) => setConfig({ ...config, resweep_days: Number(e.target.value) || 7 })}
                onBlur={(e) => updateConfig({ resweep_days: Number(e.target.value) || 7 })}
                className="w-32 bg-white border-stone-300 mt-1"
                data-testid="resweep-days-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-stone-500">Max places / query</label>
              <Input
                type="number" min={1} max={100}
                value={config.max_per_query}
                onChange={(e) => setConfig({ ...config, max_per_query: Number(e.target.value) || 20 })}
                onBlur={(e) => updateConfig({ max_per_query: Number(e.target.value) || 20 })}
                className="w-32 bg-white border-stone-300 mt-1"
                data-testid="max-per-query-input"
              />
            </div>
            <Button onClick={tickNow} className="bg-stone-900 text-amber-50 rounded-full" data-testid="tick-now-btn">
              <Sparkles className="h-4 w-4 mr-2" /> Sweep next stale city now
            </Button>
            {(status.failed ?? 0) > 0 && (
              <Button
                variant="outline"
                onClick={requeueFailed}
                className="rounded-full border-stone-300"
                data-testid="discovery-requeue-btn"
              >
                Requeue {status.failed} failed
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {recent.length > 0 && (
        <Card className="border-stone-300 bg-white/70">
          <CardHeader>
            <CardTitle className="font-serif text-xl">Recent tasks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-stone-100 text-stone-700">
                  <tr>
                    <th className="text-left px-2 py-1">Status</th>
                    <th className="text-left px-2 py-1">Query</th>
                    <th className="text-left px-2 py-1">City</th>
                    <th className="text-left px-2 py-1">Country</th>
                    <th className="text-left px-2 py-1">Places</th>
                    <th className="text-left px-2 py-1">New</th>
                    <th className="text-left px-2 py-1">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((t) => (
                    <tr key={t.id} className="border-t border-stone-200">
                      <td className="px-2 py-1">
                        <Badge
                          className={
                            t.status === "done" ? "bg-emerald-700 text-amber-50" :
                            t.status === "failed" ? "bg-red-700 text-amber-50" :
                            t.status === "running" || t.status === "claimed" ? "bg-stone-700 text-amber-50" :
                            "bg-stone-300 text-stone-800"
                          }
                        >{t.status}</Badge>
                      </td>
                      <td className="px-2 py-1 font-mono">{t.query}</td>
                      <td className="px-2 py-1">{t.city}</td>
                      <td className="px-2 py-1">{t.country}</td>
                      <td className="px-2 py-1 text-right">{t.places_returned}</td>
                      <td className="px-2 py-1 text-right">{t.new_companies}</td>
                      <td className="px-2 py-1 text-stone-500 max-w-[260px] truncate">{t.error || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// OUTREACH TAB
// ============================================================================
function OutreachTab() {
  const [status, setStatus] = useState(null);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [fromSelect, setFromSelect] = useState("");      // dropdown value ("preset" or "other")
  const [fromCustom, setFromCustom] = useState("");      // free-text when "other"
  const [tracking, setTracking] = useState(false);       // open/click tracking — off by default for deliverability
  const [approvedRows, setApprovedRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [previewRow, setPreviewRow] = useState(null);
  const [blockedDomains, setBlockedDomains] = useState([]);
  const [suppressedEmails, setSuppressedEmails] = useState([]);
  const [newBlockedDomain, setNewBlockedDomain] = useState("");
  const [savingBlocklist, setSavingBlocklist] = useState(false);
  const [warmup, setWarmup] = useState(null);

  const fromValue = fromSelect === "other" ? fromCustom : fromSelect;

  const loadWarmup = useCallback(async () => {
    try { const w = await axios.get(`${API}/outreach/warmup`); setWarmup(w.data); } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    const s = await axios.get(`${API}/outreach/status`);
    setStatus(s.data);
    if (!subject) setSubject(s.data.default_subject || "");
    if (!bodyHtml) setBodyHtml(s.data.default_body_html || "");
    if (!fromSelect) {
      const presets = s.data.from_presets || [];
      const saved = s.data.from_name || presets[0] || "";
      if (presets.includes(saved)) {
        setFromSelect(saved);
      } else {
        setFromSelect("other");
        setFromCustom(saved);
      }
    }
    // Approved contacts with an email — these are the send candidates.
    // limit=10000 (backend max) so the full approved list loads, not just 2000.
    const r = await axios.get(`${API}/crm/contacts?status=approved&limit=10000`);
    setApprovedRows((r.data.rows || []).filter((x) => (x.email || "").includes("@")));
    const m = await axios.get(`${API}/outreach/messages?limit=10000`);
    setMessages(m.data.rows || []);
    const bd = await axios.get(`${API}/outreach/blocked-domains`);
    setBlockedDomains(bd.data.domains || []);
    setSuppressedEmails(bd.data.emails || []);
    loadWarmup();
  }, [subject, bodyHtml, fromSelect, loadWarmup]);

  useEffect(() => { load(); }, [load]);
  // Poll warm-up progress more frequently so the queue drains visibly.
  useEffect(() => {
    const t = setInterval(loadWarmup, 15000);
    return () => clearInterval(t);
  }, [loadWarmup]);
  useEffect(() => {
    if (!status?.connected) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [status?.connected, load]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === filteredApproved.length) setSelected(new Set());
    else setSelected(new Set(filteredApproved.map((r) => r.id)));
  };
  const selectNextBatch = (n) => {
    // Add the first `n` currently-unselected rows to the selection.
    const next = new Set(selected);
    let added = 0;
    for (const r of filteredApproved) {
      if (added >= n) break;
      if (!next.has(r.id)) {
        next.add(r.id);
        added += 1;
      }
    }
    setSelected(next);
    if (added === 0) toast.message("Everyone is already selected");
  };

  const renderPreview = (row) => {
    const subj = (subject || "").replace(/\{first_name\}/g, row.first_name || "there")
      .replace(/\{company_name\}/g, row.company_name || "your house")
      .replace(/\{city\}/g, row.city || "your city")
      .replace(/\{sender_name\}/g, fromValue || status?.sender_name || "");
    const html = (bodyHtml || "").replace(/\{first_name\}/g, row.first_name || "there")
      .replace(/\{company_name\}/g, row.company_name || "your house")
      .replace(/\{city\}/g, row.city || "your city")
      .replace(/\{calendly_link\}/g, status?.calendly_url || "")
      .replace(/\{sender_name\}/g, fromValue || status?.sender_name || "");
    return { subj, html };
  };

  const send = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one contact");
      return;
    }
    const wuOn = warmup?.enabled;
    const confirmMsg = wuOn
      ? `Queue ${selected.size} contacts for warm-up sending?\n\nThey'll go out at a safe, auto-ramping rate (up to ${warmup?.daily_cap}/day right now) — not all at once.`
      : `Send to ${selected.size} contacts now?`;
    if (!window.confirm(confirmMsg)) return;
    setSending(true);
    const toastId = toast.loading(wuOn ? `Queuing ${selected.size}…` : `Sending to ${selected.size} contacts…`);
    try {
      const r = await axios.post(`${API}/outreach/send`, {
        contact_ids: Array.from(selected),
        subject,
        body_html: bodyHtml,
        campaign_name: campaignName || undefined,
        from_name: fromValue || undefined,
        track: tracking,
      });
      setSelected(new Set());
      // Warm-up mode: contacts were queued, the scheduler drips them out.
      if (r.data.mode === "warmup") {
        const w = r.data.warmup || {};
        const days = w.est_days_to_clear || 0;
        toast.success(`${r.data.queued} added to the warm-up queue`, {
          id: toastId,
          description: `Sending up to ${w.daily_cap}/day (${w.remaining_today} more today). These will go out over ~${days || 1} day${(days || 1) === 1 ? "" : "s"}.`,
        });
        setSending(false);
        setWarmup(w);
        load();
        return;
      }
      const jobId = r.data.job_id;
      // Immediate mode — poll job status
      const poll = setInterval(async () => {
        try {
          const j = await axios.get(`${API}/outreach/send/${jobId}`);
          const s = j.data;
          toast.loading(
            `Sending ${s.processed}/${s.total} — sent ${s.sent}, failed ${s.failed}`,
            { id: toastId },
          );
          if (s.status === "done") {
            clearInterval(poll);
            toast.success(`Campaign finished: ${s.sent} sent, ${s.failed} failed, ${s.skipped} skipped`, { id: toastId });
            setSending(false);
            load();
          }
        } catch {/* ignore poll errors */}
      }, 4000);
    } catch (e) {
      toast.error("Send failed", {
        id: toastId,
        description: e?.response?.data?.detail || e.message,
      });
      setSending(false);
    }
  };

  const connectGmail = () => {
    window.location.href = `${API}/oauth/gmail/login`;
  };
  const disconnect = async () => {
    await axios.post(`${API}/oauth/gmail/disconnect`);
    load();
  };
  const pollReplies = async () => {
    const r = await axios.post(`${API}/outreach/poll-replies`);
    const rep = r.data.newly_replied ?? 0;
    const bnc = r.data.newly_bounced ?? 0;
    toast.success(`${rep} new replies · ${bnc} new bounces detected`);
    load();
  };

  // Auto-poll replies + bounces every 5 minutes while connected
  useEffect(() => {
    if (!status?.connected) return undefined;
    const id = setInterval(async () => {
      try { await axios.post(`${API}/outreach/poll-replies`); load(); } catch {/* ignore */}
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [status?.connected, load]);

  const [savingTemplate, setSavingTemplate] = useState(false);
  const saveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await axios.put(`${API}/outreach/template`, { subject, body_html: bodyHtml, from_name: fromValue });
      toast.success("Template saved");
      load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSavingTemplate(false);
    }
  };
  const resetTemplate = async () => {
    if (!window.confirm("Reset the template to the built-in default? Your current edits will be lost.")) return;
    try {
      const r = await axios.post(`${API}/outreach/template/reset`);
      setSubject(r.data.subject);
      setBodyHtml(r.data.body_html);
      toast.success("Template reset to default");
      load();
    } catch (e) {
      toast.error("Reset failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const saveBlocklist = async (domains, emails) => {
    setSavingBlocklist(true);
    try {
      const r = await axios.put(`${API}/outreach/blocked-domains`, { domains, emails });
      setBlockedDomains(r.data.domains || []);
      setSuppressedEmails(r.data.emails || []);
      toast.success("Suppression list updated");
      setSelected(new Set()); // clear any now-blocked selections
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSavingBlocklist(false);
    }
  };
  const addSuppression = () => {
    const raw = newBlockedDomain.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/^@/, "").split("/")[0];
    if (!raw) return;
    if (raw.includes("@")) {
      if (suppressedEmails.includes(raw)) { toast.error("Already suppressed"); return; }
      saveBlocklist(blockedDomains, [...suppressedEmails, raw]);
    } else {
      if (blockedDomains.includes(raw)) { toast.error("Already blocked"); return; }
      saveBlocklist([...blockedDomains, raw], suppressedEmails);
    }
    setNewBlockedDomain("");
  };
  const removeDomain = (d) => saveBlocklist(blockedDomains.filter((x) => x !== d), suppressedEmails);
  const removeEmail = (e) => saveBlocklist(blockedDomains, suppressedEmails.filter((x) => x !== e));

  const isBlocked = (email) => {
    const e = (email || "").toLowerCase();
    if (suppressedEmails.includes(e)) return true;
    const dom = e.split("@")[1] || "";
    return blockedDomains.some((d) => dom === d || dom.endsWith("." + d));
  };
  const filteredApproved = approvedRows.filter((r) => !isBlocked(r.email));

  const resendFromLog = async (msg) => {
    if (!msg.contact_id) {
      toast.error("Missing contact id on this log entry");
      return;
    }
    if (!window.confirm(`Resend the outreach email to ${msg.to_email}?\n\nThis will create a new entry in the Sent log.`)) return;
    const toastId = toast.loading(`Resending to ${msg.to_email}…`);
    try {
      const r = await axios.post(`${API}/outreach/send`, {
        contact_ids: [msg.contact_id],
        subject,
        body_html: bodyHtml,
        campaign_name: `Resend · ${msg.to_email}`,
        from_name: fromValue || undefined,
      });
      const jobId = r.data.job_id;
      const poll = setInterval(async () => {
        try {
          const j = await axios.get(`${API}/outreach/send/${jobId}`);
          const s = j.data;
          if (s.status === "done") {
            clearInterval(poll);
            const res = (s.results || [])[0];
            if (res?.status === "sent") {
              toast.success(`Resent to ${msg.to_email}`, { id: toastId });
              load();
            } else if (res?.status === "skipped") {
              toast.error("Skipped", { id: toastId, description: res.reason || "" });
            } else {
              toast.error("Send failed", { id: toastId, description: res?.error || "Unknown error" });
            }
          }
        } catch {/* ignore */}
      }, 2000);
    } catch (e) {
      toast.error("Send failed", { id: toastId, description: e?.response?.data?.detail || e.message });
    }
  };

  const openRate = status?.sent ? Math.round((status.unique_opens / status.sent) * 100) : 0;
  const clickRate = status?.sent ? Math.round((status.unique_clicks / status.sent) * 100) : 0;
  const replyRate = status?.sent ? Math.round((status.replies / status.sent) * 100) : 0;
  const bounceRate = status?.sent ? Math.round(((status.bounced ?? 0) / status.sent) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Connection bar */}
      <Card className="border-stone-300 bg-white/70">
        <CardContent className="py-4 flex items-center gap-4 flex-wrap">
          {status?.connected ? (
            <>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                <span className="text-sm text-stone-700">
                  Connected as <strong>{status.account?.email}</strong>
                </span>
              </div>
              <Button size="sm" variant="outline" onClick={pollReplies} className="rounded-full border-stone-300" data-testid="poll-replies-btn">
                <RefreshCw className="h-4 w-4 mr-1" /> Check replies
              </Button>
              <Button size="sm" variant="outline" onClick={disconnect} className="rounded-full border-stone-300" data-testid="disconnect-gmail-btn">
                Disconnect
              </Button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-muted-foreground/50"></span>
                <span className="text-sm text-muted-foreground">Gmail not connected</span>
              </div>
              <Button size="sm" onClick={connectGmail} className="bg-primary text-primary-foreground rounded-full" data-testid="connect-gmail-btn">
                <Mail className="h-4 w-4 mr-2" /> Connect Gmail
              </Button>
            </>
          )}
          <span className="text-xs text-stone-500 ml-auto">
            Calendly: <a className="underline" href={status?.calendly_url} target="_blank" rel="noreferrer">{status?.calendly_url}</a>
          </span>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <SummaryStat label="Sent" value={status?.sent ?? 0} />
        <SummaryStat label="Unique opens" value={`${status?.unique_opens ?? 0} (${openRate}%)`} />
        <SummaryStat label="Unique clicks" value={`${status?.unique_clicks ?? 0} (${clickRate}%)`} />
        <SummaryStat label="Replies" value={`${status?.replies ?? 0} (${replyRate}%)`} />
        <SummaryStat label="Bounced" value={`${status?.bounced ?? 0} (${bounceRate}%)`} />
        <SummaryStat label="Approved & emailable" value={filteredApproved.length} />
      </div>

      {/* Composer */}
      <Card className="border-stone-300 bg-white/70">
        <CardHeader>
          <CardTitle className="font-serif text-2xl flex items-center justify-between flex-wrap gap-3">
            <span>Compose campaign</span>
            <div className="flex items-center gap-2">
              {status?.template_saved_at && (
                <span className="text-xs text-stone-500 font-sans">
                  Saved {new Date(status.template_saved_at).toLocaleString()}
                </span>
              )}
              <Button size="sm" variant="outline" onClick={resetTemplate} className="rounded-full border-stone-300" data-testid="reset-template-btn">
                Reset to default
              </Button>
              <Button size="sm" onClick={saveTemplate} disabled={savingTemplate} className="bg-stone-900 text-amber-50 rounded-full" data-testid="save-template-btn">
                {savingTemplate ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Save template"}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="block text-xs uppercase tracking-wider text-stone-500">From sender name</label>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={fromSelect} onValueChange={setFromSelect}>
              <SelectTrigger className="w-72" data-testid="from-select">
                <SelectValue placeholder="Choose a sender…" />
              </SelectTrigger>
              <SelectContent>
                {(status?.from_presets || []).map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
                <SelectItem value="other">Other (custom)</SelectItem>
              </SelectContent>
            </Select>
            {fromSelect === "other" && (
              <Input
                value={fromCustom}
                onChange={(e) => setFromCustom(e.target.value)}
                placeholder='e.g. "Maria from Apparel-OS"'
                className="max-w-sm"
                data-testid="from-custom-input"
              />
            )}
            <span className="text-xs text-stone-500">
              Will appear as: <strong>{fromValue || "—"}</strong> &lt;{status?.account?.email || "hello@apparel-os.com"}&gt;
            </span>
          </div>
          <label className="block text-xs uppercase tracking-wider text-stone-500">Campaign name (internal)</label>
          <Input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="e.g. London-Aug-Wave-1" data-testid="campaign-name-input" />
          <label className="block text-xs uppercase tracking-wider text-stone-500">Subject</label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="subject-input" />
          <label className="block text-xs uppercase tracking-wider text-stone-500">Body HTML</label>
          <textarea
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            rows={14}
            className="w-full text-sm font-mono border border-stone-300 rounded-md px-3 py-2 bg-white"
            data-testid="body-textarea"
          />
          <p className="text-xs text-stone-500">
            Merge tags: <code>{"{first_name}"}</code> <code>{"{company_name}"}</code> <code>{"{city}"}</code> <code>{"{calendly_link}"}</code>
          </p>
        </CardContent>
      </Card>

      {/* Suppression list — domains + emails */}
      <Card className="border-stone-300 bg-white/70">
        <CardHeader>
          <CardTitle className="font-serif text-2xl">Do not send to these domains or emails</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              value={newBlockedDomain}
              onChange={(e) => setNewBlockedDomain(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSuppression(); } }}
              placeholder="Enter a domain (e.g. gmail.com) or a full email (e.g. spam@foo.com)"
              className="max-w-lg"
              data-testid="blocked-domain-input"
            />
            <Button size="sm" onClick={addSuppression} disabled={savingBlocklist || !newBlockedDomain.trim()} className="rounded-full bg-stone-900 text-amber-50" data-testid="add-blocked-btn">
              Add
            </Button>
            <span className="text-xs text-stone-500">
              An address with <code>@</code> is added to the email list; anything else is added as a domain (matches subdomains too).
            </span>
          </div>

          {(blockedDomains.length + suppressedEmails.length) === 0 ? (
            <p className="text-sm text-stone-500 italic">Nothing suppressed yet.</p>
          ) : (
            <div className="space-y-3">
              {blockedDomains.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">Blocked domains ({blockedDomains.length})</p>
                  <div className="flex flex-wrap gap-2" data-testid="blocked-domains-list">
                    {blockedDomains.map((d) => (
                      <span key={d} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-stone-100 border border-stone-300 text-sm">
                        {d}
                        <button onClick={() => removeDomain(d)} className="text-stone-500 hover:text-red-700 text-xs ml-1" title="Remove" data-testid={`remove-blocked-${d}`}>×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {suppressedEmails.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">Suppressed emails ({suppressedEmails.length})</p>
                  <div className="flex flex-wrap gap-2" data-testid="suppressed-emails-list">
                    {suppressedEmails.map((e) => (
                      <span key={e} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-50 border border-red-200 text-sm font-mono text-xs">
                        {e}
                        <button onClick={() => removeEmail(e)} className="text-red-500 hover:text-red-700 text-xs ml-1" title="Remove" data-testid={`remove-suppressed-${e}`}>×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Warm-up scheduler */}
      <Card className="border-stone-300 bg-white/70">
        <CardHeader>
          <CardTitle className="font-serif text-2xl flex items-center justify-between flex-wrap gap-3">
            <span>Warm-up {warmup?.enabled
              ? <Badge variant="gold" className="ml-2 align-middle">ON</Badge>
              : <Badge variant="secondary" className="ml-2 align-middle">off</Badge>}</span>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm font-sans text-muted-foreground cursor-pointer" title="Only send Monday–Friday. When off, sends also go out on weekends (still within daytime hours).">
                <input type="checkbox" checked={warmup?.weekdays_only ?? true} data-testid="warmup-weekdays-toggle"
                  onChange={async (e) => { try { const w = await axios.put(`${API}/outreach/warmup`, { weekdays_only: e.target.checked }); setWarmup(w.data); } catch { /* ignore */ } }}
                  className="accent-bronze" />
                Weekdays only
              </label>
              <label className="flex items-center gap-2 text-sm font-sans text-muted-foreground cursor-pointer" title="When on, sends are metered to a safe, auto-ramping daily rate and the rest is queued.">
                <input type="checkbox" checked={!!warmup?.enabled} data-testid="warmup-toggle"
                  onChange={async (e) => { try { const w = await axios.put(`${API}/outreach/warmup`, { enabled: e.target.checked }); setWarmup(w.data); } catch { /* ignore */ } }}
                  className="accent-bronze" />
                Auto-meter sends
              </label>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {warmup?.enabled ? (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-6">
                <div><div className="text-xs uppercase tracking-wider text-stone-500">Today</div><div className="text-xl font-serif">{warmup.sent_today}/{warmup.daily_cap}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-stone-500">In queue</div><div className="text-xl font-serif">{warmup.queued}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-stone-500">Clears in</div><div className="text-xl font-serif">{warmup.queued > 0 ? `~${warmup.est_days_to_clear || 1}d` : "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-stone-500">Stage</div><div className="text-xl font-serif">{warmup.started_at ? `Day ${warmup.day} · Wk ${warmup.week}` : "Not started"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-stone-500">Window</div><div className={`text-xl font-serif ${warmup.within_send_window ? "text-emerald-700" : "text-stone-400"}`}>{warmup.within_send_window ? "Sending" : "Paused"}</div></div>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-bronze transition-all" style={{ width: `${warmup.daily_cap ? Math.min(100, Math.round((warmup.sent_today / warmup.daily_cap) * 100)) : 0}%` }} />
              </div>
              <p className="text-sm text-muted-foreground">
                However many you queue, the CRM sends up to <strong>{warmup.daily_cap}/day</strong> ({warmup.remaining_today} more today), then trickles the rest over the following days — ramping to <strong>{warmup.next_week_cap}/day</strong> next week.{warmup.send_window && <> Sending only during <strong>{warmup.send_window}</strong>{!warmup.within_send_window && <span className="text-stone-500"> — currently paused until the next window</span>}.</>}{!warmup.connected && <span className="text-red-600"> Connect Gmail to send.</span>}
              </p>
              <div className="flex flex-wrap gap-2">
                {(warmup.schedule || []).map((s) => (
                  <span key={s.week} className={`text-xs rounded-full px-2 py-0.5 border ${s.week === warmup.week ? "border-gold bg-gold/15 text-bronze" : "border-stone-300 text-stone-500"}`}>Wk {s.week}: {s.cap}/day</span>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Warm-up is <strong>off</strong> — sends go out immediately. Turn it on to auto-meter sends to a safe, ramping daily rate (ideal for a new sending domain).</p>
          )}
        </CardContent>
      </Card>

      {/* Recipients */}
      <Card className="border-stone-300 bg-white/70">
        <CardHeader>
          <CardTitle className="font-serif text-2xl flex items-center justify-between flex-wrap gap-3">
            <span>Recipients <span className="text-sm text-stone-500 font-sans">({selected.size} selected of {filteredApproved.length} approved{blockedDomains.length > 0 && approvedRows.length !== filteredApproved.length ? ` · ${approvedRows.length - filteredApproved.length} blocked` : ""})</span></span>
            <div className="flex gap-2 flex-wrap items-center">
              <label className="flex items-center gap-1.5 text-xs font-sans text-muted-foreground cursor-pointer select-none mr-1" title="Adds an open pixel + rewrites links. Improves stats but hurts deliverability on cold sends — leave off for first-touch.">
                <input type="checkbox" checked={tracking} onChange={(e) => setTracking(e.target.checked)} data-testid="tracking-toggle" className="accent-bronze" />
                Track opens/clicks
              </label>
              <Button size="sm" variant="outline" className="rounded-full border-stone-300" onClick={() => selectNextBatch(100)} data-testid="select-next-100-btn">
                + Select next 100
              </Button>
              <Button size="sm" variant="outline" className="rounded-full border-stone-300" onClick={toggleAll} data-testid="select-all-btn">
                {selected.size === filteredApproved.length ? "Clear" : "Select all"}
              </Button>
              <Button size="sm" disabled={!status?.connected || sending || selected.size === 0} onClick={send} title={!status?.connected ? "Connect Gmail first" : (selected.size === 0 ? "Select at least one recipient" : "Send campaign")} className="bg-primary text-primary-foreground rounded-full" data-testid="send-campaign-btn">
                {sending ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Sending…</> : <><Play className="h-4 w-4 mr-1" /> Send to {selected.size}</>}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto border border-stone-200 rounded-lg max-h-[400px]">
            <table className="min-w-full text-sm" data-testid="recipients-table">
              <thead className="bg-stone-100 text-stone-700 sticky top-0">
                <tr>
                  <th className="px-3 py-2 w-8"></th>
                  <th className="text-left px-3 py-2">Company</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">City</th>
                  <th className="text-right px-3 py-2 w-20">Preview</th>
                </tr>
              </thead>
              <tbody>
                {filteredApproved.slice(0, 500).map((r) => (
                  <tr key={r.id} className={`border-t border-stone-200 hover:bg-stone-50 ${selected.has(r.id) ? "bg-amber-50/50" : ""}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`recipient-check-${r.id}`} />
                    </td>
                    <td className="px-3 py-2 font-medium">{r.company_name}</td>
                    <td className="px-3 py-2">{(r.first_name || "") + " " + (r.last_name || "")}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.email}</td>
                    <td className="px-3 py-2">{r.city}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setPreviewRow(r)} className="text-xs underline text-stone-700" data-testid={`preview-${r.id}`}>Preview</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Messages log */}
      <Card className="border-stone-300 bg-white/70">
        <CardHeader>
          <CardTitle className="font-serif text-2xl">Sent log</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto border border-stone-200 rounded-lg max-h-[400px]">
            <table className="min-w-full text-sm" data-testid="messages-table">
              <thead className="bg-stone-100 text-stone-700 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">To</th>
                  <th className="text-left px-3 py-2">Subject</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Opens</th>
                  <th className="text-right px-3 py-2">Clicks</th>
                  <th className="text-left px-3 py-2">Replied</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {messages.length === 0 && (
                  <tr><td colSpan={8} className="text-center px-3 py-6 text-stone-500 text-sm">No messages sent yet.</td></tr>
                )}
                {messages.map((m) => (
                  <tr key={m.id} className="border-t border-stone-200 hover:bg-stone-50">
                    <td className="px-3 py-2 text-xs">{m.sent_at ? new Date(m.sent_at).toLocaleString() : ""}</td>
                    <td className="px-3 py-2 font-mono text-xs">{m.to_email}</td>
                    <td className="px-3 py-2">{m.subject}</td>
                    <td className="px-3 py-2 text-xs">
                      {m.status === "bounced" ? (
                        <Badge className="bg-red-700 text-amber-50">Bounced</Badge>
                      ) : m.status === "sent" ? (
                        <Badge className="bg-emerald-700 text-amber-50">Sent</Badge>
                      ) : (
                        <Badge className="bg-rose-700 text-amber-50">{m.status}</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">{m.opens || 0}</td>
                    <td className="px-3 py-2 text-right">{m.clicks || 0}</td>
                    <td className="px-3 py-2">
                      {m.status === "bounced" ? (
                        <Badge className="bg-red-700 text-amber-50">Bounced</Badge>
                      ) : m.replied ? (
                        <Badge className="bg-amber-700 text-amber-50">replied</Badge>
                      ) : ""}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => resendFromLog(m)}
                        disabled={!status?.connected || !m.contact_id}
                        data-testid={`resend-${m.id}`}
                        className="px-2 py-1 rounded-full text-xs bg-primary text-primary-foreground hover:bg-primary/80 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={!status?.connected ? "Connect Gmail first to send" : (!m.contact_id ? "No linked contact for this message" : "Send this email again")}
                      >
                        <Mail className="inline-block h-3 w-3 mr-1" />
                        Resend
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Preview modal */}
      {previewRow && (
        <div className="fixed inset-0 bg-stone-900/40 z-50 flex items-center justify-center" onClick={() => setPreviewRow(null)}>
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-stone-200 px-5 py-3 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-stone-500">Preview · {previewRow.company_name}</p>
                <p className="font-serif text-lg">{renderPreview(previewRow).subj}</p>
                <p className="text-xs text-stone-500">To: {previewRow.email}</p>
              </div>
              <button onClick={() => setPreviewRow(null)} className="text-stone-500 hover:text-stone-900 text-xl">×</button>
            </div>
            <div className="p-5 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: renderPreview(previewRow).html }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// INSTAGRAM TAB
// ============================================================================
// Mirrors the server-side token substitution in instagram.js.
function igRenderMessage(message, t) {
  const full = (t.full_name || "").trim();
  const first = full.split(/\s+/)[0] || "";
  const handle = t.handle ? `@${t.handle}` : "";
  const name = first || full || (t.handle || "there");
  return String(message || "")
    .replace(/\{\{\s*first_name\s*\}\}/gi, first)
    .replace(/\{\{\s*full_name\s*\}\}/gi, full)
    .replace(/\{\{\s*handle\s*\}\}/gi, handle)
    .replace(/\{\{\s*name\s*\}\}/gi, name);
}

const IG_STATUSES = ["new", "approved", "rejected", "contacted"];

function InstagramTab() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fStatus, setFStatus] = useState("new");
  const [fSearch, setFSearch] = useState("");
  const [fMinFollowers, setFMinFollowers] = useState("");

  const [message, setMessage] = useState("");
  const [dailyTarget, setDailyTarget] = useState(25);
  const [savingTpl, setSavingTpl] = useState(false);

  const [hashtags, setHashtags] = useState("");
  const [handles, setHandles] = useState("");
  const [bioKeywords, setBioKeywords] = useState("");
  const [resultsLimit, setResultsLimit] = useState(50);
  const [job, setJob] = useState(null);

  const [competitors, setCompetitors] = useState([]);
  const [newComp, setNewComp] = useState("");
  const [scrapeFollowers, setScrapeFollowers] = useState(false);
  const [followersLimit, setFollowersLimit] = useState(1000);

  const loadCompetitors = useCallback(async () => {
    try { const r = await axios.get(`${API}/instagram/competitors`); setCompetitors(r.data.rows || []); } catch { /* noop */ }
  }, []);
  useEffect(() => { loadCompetitors(); }, [loadCompetitors]);

  const addComp = async () => {
    const list = newComp.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) return;
    try {
      const r = await axios.post(`${API}/instagram/competitors`, { handles: list });
      setNewComp("");
      loadCompetitors();
      toast.success(`${r.data.added} competitor${r.data.added === 1 ? "" : "s"} saved`);
    } catch { toast.error("Couldn't save competitor(s)"); }
  };
  const removeComp = async (id) => {
    try { await axios.delete(`${API}/instagram/competitors/${id}`); loadCompetitors(); } catch { /* noop */ }
  };

  const [followTarget, setFollowTarget] = useState(15);

  // Follow assist: open the profile, log it, mark the row followed in place.
  const follow = (t) => {
    window.open(`https://instagram.com/${t.handle}`, "_blank", "noopener");
    axios.post(`${API}/instagram/targets/${t.id}/followed`)
      .then(() => { setRows((prev) => prev.map((r) => (r.id === t.id ? { ...r, followed_at: "now" } : r))); loadStats(); })
      .catch(() => {});
    toast.success(`Opened @${t.handle} — tap Follow on Instagram`, { description: "Logged as followed. Keep it human — one at a time." });
  };

  const loadStats = useCallback(async () => {
    try { const r = await axios.get(`${API}/instagram/stats`); setStats(r.data); } catch { /* noop */ }
  }, []);

  const loadTargets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fStatus !== "all") params.set("status", fStatus);
      if (fSearch) params.set("search", fSearch);
      if (fMinFollowers) params.set("min_followers", fMinFollowers);
      const r = await axios.get(`${API}/instagram/targets?${params.toString()}`);
      setRows(r.data.rows || []);
    } catch { toast.error("Failed to load Instagram targets"); }
    finally { setLoading(false); }
  }, [fStatus, fSearch, fMinFollowers]);

  useEffect(() => { loadTargets(); }, [loadTargets]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/instagram/template`);
        setMessage(r.data.message || "");
        setDailyTarget(r.data.daily_target || 25);
        setFollowTarget(r.data.follow_daily_target || 15);
      } catch { /* noop */ }
    })();
  }, []);

  // Poll a running discovery job.
  useEffect(() => {
    if (!job || job.status === "done" || job.status === "error") return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/crm/enrich/jobs/${job.id}`);
        setJob(r.data);
        if (r.data.status === "done" || r.data.status === "error") {
          clearInterval(t);
          toast(r.data.status === "done" ? "Discovery complete" : "Discovery failed", { description: r.data.message });
          loadTargets(); loadStats();
        }
      } catch { /* noop */ }
    }, 3500);
    return () => clearInterval(t);
  }, [job?.id, job?.status, loadTargets, loadStats]);

  const saveTemplate = async () => {
    setSavingTpl(true);
    try {
      await axios.put(`${API}/instagram/template`, { message, daily_target: Number(dailyTarget), follow_daily_target: Number(followTarget) });
      toast.success("Instagram template saved");
      loadStats();
    } catch { toast.error("Failed to save template"); }
    finally { setSavingTpl(false); }
  };

  const discover = async () => {
    // Merge the ad-hoc handles field with all saved competitor handles.
    const allHandles = [handles, ...competitors.map((c) => c.handle)].filter(Boolean).join(",");
    if (!hashtags.trim() && !allHandles.trim()) { toast.error("Add at least one hashtag or competitor handle"); return; }
    try {
      const r = await axios.post(`${API}/instagram/discover`, {
        hashtags, handles: allHandles, bio_keywords: bioKeywords, results_limit: Number(resultsLimit),
        scrape_followers: scrapeFollowers, followers_limit: Number(followersLimit),
      });
      setJob(r.data);
      toast("Discovery started", { description: r.data.message });
    } catch (e) { toast.error(e.response?.data?.error?.message || "Failed to start discovery"); }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/instagram/targets/${id}/status`, { status });
      setRows((prev) => (fStatus === "all" || fStatus === status
        ? prev.map((r) => (r.id === id ? { ...r, status } : r))
        : prev.filter((r) => r.id !== id)));
      loadStats();
    } catch { toast.error("Failed to update status"); }
  };

  // Compliant DM assist: copy the personalised message, open the DM thread, log it.
  const dm = (t) => {
    const msg = igRenderMessage(message, t);
    try { navigator.clipboard.writeText(msg).catch(() => {}); } catch { /* noop */ }
    window.open(`https://ig.me/m/${t.handle}`, "_blank", "noopener");
    axios.post(`${API}/instagram/targets/${t.id}/contacted`).then(() => {
      // Keep the row in place (marked DM'd) so you can still follow it too.
      setRows((prev) => prev.map((r) => (r.id === t.id ? { ...r, status: "contacted" } : r)));
      loadStats();
    }).catch(() => {});
    toast.success(`Message copied — paste into @${t.handle}'s DM`, {
      description: "Opens Instagram in a new tab. Send it there to stay compliant.",
    });
  };

  const skip = (t) => setStatus(t.id, "rejected"); // removes from the list for good

  const remaining = stats ? stats.remaining_today : 0;
  const pct = stats && stats.daily_target ? Math.min(100, Math.round((stats.contacted_today / stats.daily_target) * 100)) : 0;
  const followRemaining = stats ? (stats.follow_remaining_today ?? 0) : 0;
  const followPct = stats && stats.follow_daily_target ? Math.min(100, Math.round((stats.followed_today / stats.follow_daily_target) * 100)) : 0;

  return (
    <div className="space-y-6">
      {/* Daily pace meters — follow + DM */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Instagram className="h-4 w-4" /> Instagram outreach
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <SummaryStat icon={<Users className="h-4 w-4" />} label="Total accounts" value={stats?.total ?? "—"} />
            <SummaryStat icon={<Instagram className="h-4 w-4" />} label="Followed all-time" value={stats?.total_followed ?? "—"} />
            <SummaryStat icon={<CheckCircle2 className="h-4 w-4" />} label="DM'd all-time" value={stats?.by_status?.contacted ?? "—"} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="flex items-center justify-between text-xs text-stone-600 mb-1">
                <span>Followed today: {stats?.followed_today ?? 0} / {stats?.follow_daily_target ?? followTarget}</span>
                <span>{followRemaining > 0 ? `${followRemaining} left` : "target reached"}</span>
              </div>
              <Progress value={followPct} className="h-2" />
            </div>
            <div>
              <div className="flex items-center justify-between text-xs text-stone-600 mb-1">
                <span>DM'd today: {stats?.contacted_today ?? 0} / {stats?.daily_target ?? dailyTarget}</span>
                <span>{remaining > 0 ? `${remaining} left` : "target reached"}</span>
              </div>
              <Progress value={pct} className="h-2" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Discovery */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Discover relevant accounts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="text-xs text-stone-600">Hashtags (comma-separated)</label>
              <Input value={hashtags} onChange={(e) => setHashtags(e.target.value)}
                placeholder="bespoketailoring, madetomeasure, sartoria" data-testid="ig-hashtags" />
            </div>
            <div>
              <label className="text-xs text-stone-600">Competitor handles — one-off (finds similar accounts)</label>
              <Input value={handles} onChange={(e) => setHandles(e.target.value)}
                placeholder="@huntsmansavilerow, @andersonsheppard" data-testid="ig-handles" />
            </div>
            <div>
              <label className="text-xs text-stone-600">Extra keywords — narrow further (optional)</label>
              <Input value={bioKeywords} onChange={(e) => setBioKeywords(e.target.value)}
                placeholder="e.g. wedding, Milan" data-testid="ig-keywords" />
              <p className="text-[11px] text-stone-400 mt-1">
                Discovery already keeps only tailoring accounts (tailor, menswear, made-to-measure, bespoke…).
              </p>
            </div>
            <div>
              <label className="text-xs text-stone-600">Results per hashtag</label>
              <Input type="number" min="10" max="200" value={resultsLimit}
                onChange={(e) => setResultsLimit(e.target.value)} data-testid="ig-limit" />
            </div>
          </div>
          {/* Saved competitors — reused on every discovery run */}
          <div className="rounded-lg border border-stone-200 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-stone-700">Saved competitors</label>
              <span className="text-[11px] text-stone-400">— always included as seeds</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {competitors.length === 0 && (<span className="text-xs text-stone-400">None saved yet — add one below.</span>)}
              {competitors.map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1 rounded-full bg-stone-100 border border-stone-200 pl-2 pr-1 py-0.5 text-xs"
                  data-testid={`ig-comp-${c.id}`}>
                  @{c.handle}
                  <button onClick={() => removeComp(c.id)} className="text-stone-400 hover:text-red-600" title="Remove">✕</button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Input value={newComp} onChange={(e) => setNewComp(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addComp()}
                placeholder="@ateliermunro, @wearebti, @jeromeclothiers — paste several"
                className="h-8 text-xs w-full max-w-md" data-testid="ig-comp-input" />
              <Button size="sm" variant="outline" onClick={addComp}
                className="rounded-full border-stone-300 h-8" data-testid="ig-comp-add">Save</Button>
            </div>
            <label className="flex items-center gap-2 text-xs text-stone-700 pt-1">
              <input type="checkbox" checked={scrapeFollowers} onChange={(e) => setScrapeFollowers(e.target.checked)}
                data-testid="ig-scrape-followers" />
              Also scan competitors' <b>followers</b> for relevant business accounts
              <Input type="number" min="25" max="5000" value={followersLimit} disabled={!scrapeFollowers}
                onChange={(e) => setFollowersLimit(e.target.value)}
                className="h-7 w-24 text-xs ml-1" data-testid="ig-followers-limit" />
              <span className="text-stone-400">per competitor</span>
            </label>
            {scrapeFollowers && (
              <p className="text-[11px] text-stone-400">
                Followers are mostly consumers — we keep only verified accounts or those whose name/handle signals the trade
                (tailor, bespoke, menswear…), then enrich those. Expect a small fraction to be relevant; higher limits cost more Apify credits.
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={discover} disabled={job && job.status === "running"}
              className="rounded-full bg-stone-900 text-amber-50 hover:bg-stone-700" data-testid="ig-discover-btn">
              {job && job.status === "running"
                ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Discovering…</>)
                : (<><Sparkles className="h-4 w-4 mr-1" /> Discover accounts</>)}
            </Button>
            {job && (<span className="text-xs text-stone-600">{job.message}</span>)}
          </div>
        </CardContent>
      </Card>

      {/* Template */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Message template</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            className="w-full rounded-lg border border-stone-300 p-3 text-sm font-mono"
            data-testid="ig-template"
          />
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-stone-500">Tokens: {"{{name}} {{first_name}} {{full_name}} {{handle}}"}</span>
            <div className="flex items-center gap-2 ml-auto">
              <label className="text-xs text-stone-600">Follows/day</label>
              <Input type="number" min="1" max="200" value={followTarget}
                onChange={(e) => setFollowTarget(e.target.value)} className="w-16" data-testid="ig-follow-target" />
              <label className="text-xs text-stone-600 ml-1">DMs/day</label>
              <Input type="number" min="1" max="200" value={dailyTarget}
                onChange={(e) => setDailyTarget(e.target.value)} className="w-16" data-testid="ig-daily-target" />
              <Button onClick={saveTemplate} disabled={savingTpl}
                variant="outline" className="rounded-full border-stone-300" data-testid="ig-save-template">
                {savingTpl ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Targets */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <span>Accounts</span>
            <div className="flex items-center gap-2 ml-auto">
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger className="h-8 w-32 text-xs" data-testid="ig-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {IG_STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
              <Input value={fSearch} onChange={(e) => setFSearch(e.target.value)}
                placeholder="Search handle/bio" className="h-8 w-40 text-xs" data-testid="ig-search" />
              <Input type="number" value={fMinFollowers} onChange={(e) => setFMinFollowers(e.target.value)}
                placeholder="Min followers" className="h-8 w-32 text-xs" data-testid="ig-min-followers" />
              <Button size="sm" variant="outline" onClick={loadTargets}
                className="rounded-full border-stone-300 h-8" data-testid="ig-refresh">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto max-h-[60vh] rounded-lg border border-stone-200">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 text-stone-700 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Account</th>
                  <th className="text-left px-3 py-2">Followers</th>
                  <th className="text-left px-3 py-2">Bio</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-left px-3 py-2 w-28">State</th>
                  <th className="text-right px-3 py-2 w-56">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-stone-400">
                    No accounts yet — run a discovery above to find relevant tailors on Instagram.
                  </td></tr>
                )}
                {rows.map((t) => (
                  <tr key={t.id} className="border-t border-stone-200 hover:bg-stone-50" data-testid={`ig-row-${t.id}`}>
                    <td className="px-3 py-2">
                      <a href={`https://instagram.com/${t.handle}`} target="_blank" rel="noreferrer"
                        className="font-medium underline text-stone-800">@{t.handle}</a>
                      {t.is_verified && <Badge className="ml-1 bg-sky-600 text-white align-middle text-[10px]">✓</Badge>}
                      <div className="text-xs text-stone-500">{t.full_name}</div>
                    </td>
                    <td className="px-3 py-2 tabular-nums">{Number(t.followers).toLocaleString()}</td>
                    <td className="px-3 py-2 max-w-[280px] text-xs text-stone-600 truncate" title={t.biography}>{t.biography}</td>
                    <td className="px-3 py-2 text-xs text-stone-500">{t.source}{t.source_seed ? <div className="text-stone-400">{t.source_seed}</div> : null}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-0.5">
                        {t.followed_at && <Badge className="bg-purple-100 text-purple-700 text-[10px] w-fit">Followed</Badge>}
                        {t.status === "contacted" && <Badge className="bg-pink-100 text-pink-700 text-[10px] w-fit">DM'd</Badge>}
                        {!t.followed_at && t.status !== "contacted" && <span className="text-[11px] text-stone-300">—</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => skip(t)}
                          className="px-2.5 py-1 rounded-full text-xs bg-stone-100 text-stone-600 hover:bg-stone-200"
                          title="Not a target — remove for good" data-testid={`ig-skip-${t.id}`}>Skip</button>
                        <button onClick={() => follow(t)} disabled={!!t.followed_at}
                          className={`px-2.5 py-1 rounded-full text-xs inline-flex items-center gap-1 ${t.followed_at ? "bg-purple-50 text-purple-400 cursor-default" : "bg-purple-600 text-white hover:bg-purple-700"}`}
                          title={t.followed_at ? "Already followed" : `Open @${t.handle} and follow`} data-testid={`ig-follow-${t.id}`}>
                          {t.followed_at ? "Following ✓" : "Follow"}
                        </button>
                        <button onClick={() => dm(t)}
                          className="px-2.5 py-1 rounded-full text-xs bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:opacity-90 inline-flex items-center gap-1"
                          title={`Copy message + open DM to @${t.handle}`} data-testid={`ig-dm-${t.id}`}>
                          <Instagram className="h-3 w-3" /> {t.status === "contacted" ? "DM again" : "DM"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-stone-400">
            One worklist per account: <strong>Skip</strong> removes it, <strong>Follow</strong> opens the profile to follow,
            <strong> DM</strong> copies your message and opens the thread. Both stay manual on purpose — automating follows or
            cold DMs violates Instagram's terms and gets accounts banned. Watch the daily meters above to stay block-safe.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// SHELL
// ============================================================================
function Console({ authEmail }) {
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem("aos-tab") || "scraper"; } catch { return "scraper"; }
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-5 flex items-center gap-4">
          <Logo className="h-10 w-10 shrink-0" />
          <div className="flex items-baseline gap-3 flex-wrap">
            <LogoWordmark />
            <span className="text-[0.7rem] font-medium uppercase tracking-[0.18em] text-bronze border border-gold/50 rounded-full px-2 py-0.5">
              CRM
            </span>
            <p className="w-full sm:w-auto text-sm text-muted-foreground sm:ml-1" data-testid="page-title">
              Bespoke-tailoring intelligence: discover, verify, and run outreach.
            </p>
          </div>
          {authEmail && (
            <div className="ml-auto flex items-center gap-3 self-center shrink-0">
              <span className="text-xs text-muted-foreground hidden md:inline">{authEmail}</span>
              <Button
                variant="outline" size="sm" className="rounded-full"
                onClick={async () => { try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ } window.location.reload(); }}
                data-testid="sign-out-btn"
              >
                Sign out
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-8">
        <Tabs
          value={activeTab}
          onValueChange={(v) => { setActiveTab(v); try { localStorage.setItem("aos-tab", v); } catch {/* ignore */} }}
          className="w-full"
        >
          <TabsList className="bg-muted mb-6" data-testid="main-tabs">
            <TabsTrigger value="scraper" data-testid="tab-scraper">Scraper</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
            <TabsTrigger value="discovery" data-testid="tab-discovery">Discovery</TabsTrigger>
            <TabsTrigger value="outreach" data-testid="tab-outreach">Outreach</TabsTrigger>
            <TabsTrigger value="instagram" data-testid="tab-instagram">Instagram</TabsTrigger>
          </TabsList>
          <TabsContent value="scraper">
            <ScraperTab onScrapeDone={() => setRefreshSignal((v) => v + 1)} />
          </TabsContent>
          <TabsContent value="contacts">
            <ContactsTab refreshSignal={refreshSignal} />
          </TabsContent>
          <TabsContent value="discovery">
            <DiscoveryTab refreshSignal={refreshSignal} />
          </TabsContent>
          <TabsContent value="outreach">
            <OutreachTab />
          </TabsContent>
          <TabsContent value="instagram">
            <InstagramTab />
          </TabsContent>
        </Tabs>
      </main>

      <Toaster
        richColors
        position="top-right"
        toastOptions={{ style: { fontFamily: "var(--font-sans)", borderRadius: "0.65rem" } }}
      />
    </div>
  );
}

function SignInGate() {
  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
      <div className="w-full max-w-sm text-center">
        <div className="flex justify-center mb-6"><Logo className="h-14 w-14" /></div>
        <div className="flex items-center justify-center mb-2"><LogoWordmark /></div>
        <p className="text-sm text-muted-foreground mb-8">
          Bespoke-tailoring intelligence — sign in to continue.
        </p>
        <a href={`${API}/auth/login`} data-testid="signin-google">
          <Button size="lg" className="w-full rounded-full bg-primary text-primary-foreground">
            <Mail className="h-4 w-4 mr-2" /> Sign in with Google
          </Button>
        </a>
        <p className="text-xs text-muted-foreground mt-6">Access is restricted to authorised accounts.</p>
      </div>
    </div>
  );
}

function App() {
  const [auth, setAuth] = useState({ loading: true, enabled: false, authed: false, email: null });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authParam = params.get("auth");
    if (authParam === "denied") {
      toast.error("Access denied", { description: `${params.get("email") || "That account"} isn't on the allowlist.` });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (authParam === "error") {
      toast.error("Sign-in failed", { description: "Please try again." });
      window.history.replaceState({}, "", window.location.pathname);
    }
    const gmail = params.get("gmail");
    if (gmail === "connected") {
      toast.success("Gmail connected", { description: params.get("email") || "" });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (gmail === "error") {
      const reason = params.get("reason") || "";
      toast.error("Couldn't connect Gmail", {
        description: reason === "missing_credentials"
          ? "Google OAuth isn't configured yet — set GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET in backend/.env."
          : reason,
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
    axios.get(`${API}/auth/me`)
      .then((r) => setAuth({ loading: false, enabled: r.data.enabled, authed: r.data.authenticated, email: r.data.email }))
      .catch(() => setAuth({ loading: false, enabled: false, authed: false, email: null }));
  }, []);

  if (auth.loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (auth.enabled && !auth.authed) return <SignInGate />;

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Console authEmail={auth.enabled ? auth.email : null} />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
